import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import Draggable from "react-draggable";
import { classNames } from "Utils/utils";
import Vertex360CopilotDrawer from "./Vertex360CopilotDrawer";
import Vertex360CopilotFloatingButton from "./components/Vertex360CopilotFloatingButton";
import { useVertex360CopilotFloatingVisibility } from "./hooks/useVertex360CopilotFloatingVisibility";
import { useLivechatDrawerOffset } from "./hooks/useLivechatDrawerOffset";
import {
  anchorToCopilotFloatingPixels,
  clampCopilotFloatingAnchor,
  clampCopilotFloatingPosition,
  VERTEX360_COPILOT_FLOATING_DRAG_THRESHOLD_PX,
  VERTEX360_COPILOT_FLOATING_INTRO_DISMISSED_SESSION_KEY,
  VERTEX360_COPILOT_FLOATING_Z_INDEX,
  getCopilotFloatingFormsPageAnchor,
  getDefaultCopilotFloatingAnchor,
  pixelsToCopilotFloatingAnchor,
  readCopilotFloatingAnchor,
  resolveCopilotFloatingPosition,
  VERTEX360_COPILOT_FORMS_ADMIN_ROUTES,
  writeCopilotFloatingAnchor,
} from "./constants/vertex360CopilotFloatingConstants";
import "./vertex360Copilot.css";

const isFormsAdminRoute = (pathname) =>
  VERTEX360_COPILOT_FORMS_ADMIN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

const Vertex360CopilotFloatingAssistant = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isVisible = useVertex360CopilotFloatingVisibility();
  const nodeRef = useRef(null);
  const fabRef = useRef(null);
  const dragOriginRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const suppressDrawerOpenRef = useRef(false);
  const anchorRef = useRef(
    readCopilotFloatingAnchor() || getDefaultCopilotFloatingAnchor()
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  useLivechatDrawerOffset(drawerOpen && isVisible);
  const [isDragging, setIsDragging] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [position, setPosition] = useState(() => resolveCopilotFloatingPosition());

  useEffect(() => {
    if (!isVisible) return undefined;

    if (sessionStorage.getItem(VERTEX360_COPILOT_FLOATING_INTRO_DISMISSED_SESSION_KEY)) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIntroOpen(true), 600);
    return () => window.clearTimeout(timer);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return undefined;

    if (isFormsAdminRoute(pathname)) {
      const formsAnchor = getCopilotFloatingFormsPageAnchor();
      anchorRef.current = formsAnchor;
      setPosition(anchorToCopilotFloatingPixels(formsAnchor));
      return undefined;
    }

    const saved = readCopilotFloatingAnchor() || getDefaultCopilotFloatingAnchor();
    anchorRef.current = saved;
    setPosition(anchorToCopilotFloatingPixels(saved));
    return undefined;
  }, [pathname, isVisible]);

  useEffect(() => {
    const handleResize = () => {
      const anchor = isFormsAdminRoute(pathname)
        ? getCopilotFloatingFormsPageAnchor()
        : clampCopilotFloatingAnchor(anchorRef.current);
      anchorRef.current = anchor;
      setPosition(anchorToCopilotFloatingPixels(anchor));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pathname]);

  const handleDismissIntro = useCallback(() => {
    suppressDrawerOpenRef.current = true;
    sessionStorage.setItem(VERTEX360_COPILOT_FLOATING_INTRO_DISMISSED_SESSION_KEY, "1");
    setIntroOpen(false);
    window.setTimeout(() => {
      suppressDrawerOpenRef.current = false;
    }, 320);
  }, []);

  const handleDragStart = useCallback((_event, data) => {
    dragOriginRef.current = { x: data.x, y: data.y };
    didDragRef.current = false;
    setIsDragging(true);
  }, []);

  const handleDrag = useCallback((_event, data) => {
    const dx = Math.abs(data.x - dragOriginRef.current.x);
    const dy = Math.abs(data.y - dragOriginRef.current.y);
    if (
      dx > VERTEX360_COPILOT_FLOATING_DRAG_THRESHOLD_PX ||
      dy > VERTEX360_COPILOT_FLOATING_DRAG_THRESHOLD_PX
    ) {
      didDragRef.current = true;
    }
    setPosition({ x: data.x, y: data.y });
  }, []);

  const handleDragStop = useCallback((event, data) => {
    const wasDrag = didDragRef.current;
    setIsDragging(false);

    const next = clampCopilotFloatingPosition({ x: data.x, y: data.y });
    const anchor = clampCopilotFloatingAnchor(
      pixelsToCopilotFloatingAnchor(next.x, next.y)
    );
    anchorRef.current = anchor;
    setPosition(anchorToCopilotFloatingPixels(anchor));
    writeCopilotFloatingAnchor(anchor);

    const clickedFab =
      fabRef.current &&
      (event.target === fabRef.current || fabRef.current.contains(event.target));

    if (!wasDrag && !suppressDrawerOpenRef.current && clickedFab) {
      setDrawerOpen(true);
    }

    window.requestAnimationFrame(() => {
      didDragRef.current = false;
    });
  }, []);

  const handleKeyboardOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleOpenFullscreen = useCallback(() => {
    setDrawerOpen(false);
    navigate("/copilot");
  }, [navigate]);

  if (!isVisible) return null;

  return createPortal(
    <>
      <div
        className={classNames(
          "copilot-floating-layer pointer-events-none fixed inset-0",
          drawerOpen && "copilot-floating-layer--drawer-open"
        )}
        style={{ zIndex: VERTEX360_COPILOT_FLOATING_Z_INDEX }}
        aria-hidden={drawerOpen}
      >
        {!drawerOpen ? (
          <Draggable
            nodeRef={nodeRef}
            position={position}
            bounds="parent"
            onStart={handleDragStart}
            onDrag={handleDrag}
            onStop={handleDragStop}
          >
            <div
              ref={nodeRef}
              className="pointer-events-auto absolute left-0 top-0 touch-none"
            >
              <Vertex360CopilotFloatingButton
                fabRef={fabRef}
                onKeyboardOpen={handleKeyboardOpen}
                isDragging={isDragging}
                introOpen={introOpen}
                onDismissIntro={handleDismissIntro}
              />
            </div>
          </Draggable>
        ) : null}
      </div>

      <Vertex360CopilotDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenFullscreen={handleOpenFullscreen}
      />
    </>,
    document.body
  );
};

export default Vertex360CopilotFloatingAssistant;
